import { Heart, MapPin } from 'lucide-react';
import { tr } from '@/i18n/tr';
import type { Category, Listing } from '@/types/domain';
import { PhotoFallback } from '@/components/photoFallback/photoFallback';
import { PhotoFrame } from '@/components/photoFrame/photoFrame';
import { useListingIcons } from '@/hooks/useListingIcons';
import styles from './listingCard.module.scss';
import { formatPrice } from '@/util/number';
import { produceStatusLabel } from '@/util/produceStatus';
import { formatLocation } from '@/util/formatLocation';
import { ProduceIcon } from '@/components/produceIcon/produceIcon';

export interface ListingCardProps {
  listing: Listing;
  category?: Category;
  onOpen?: (listing: Listing) => void;
  onToggleFavorite?: (listing: Listing) => void;
}

/** Vertical listing card (list/grid views: Keşfet list, favorites, producer listings). */
export function ListingCard({ listing, category, onOpen, onToggleFavorite }: ListingCardProps) {
  const iconFor = useListingIcons();

  return (
    <article className={styles.card} onClick={() => onOpen?.(listing)}>
      <div className={styles.media}>
        {listing.photos[0] ? (
          <PhotoFrame src={listing.photos[0].url} alt={listing.photos[0].alt} loading="lazy" />
        ) : (
          <PhotoFallback icon={iconFor(listing)} />
        )}
        <span className={styles.statusBadge}>
          {produceStatusLabel(listing.status, category?.name)}
        </span>
      </div>

      <button
        type="button"
        className={`${styles.heart} ${listing.isFavorite ? styles.heartOn : ''}`}
        aria-label={tr.common.favorite}
        aria-pressed={listing.isFavorite}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite?.(listing);
        }}
      >
        <Heart fill={listing.isFavorite ? 'currentColor' : 'none'} />
      </button>

      <div className={styles.body}>
        <h3 className={styles.title}>{listing.title}</h3>
        <p className={styles.meta}>
          <ProduceIcon icon={iconFor(listing)} /> {listing.productName || listing.productType} |{' '}
          {formatLocation(listing, { short: true })}
          {listing.locationPrecision === 'approximate' && (
            <span className={styles.approximateBadge}>{tr.createListing.locationApproximate}</span>
          )}
        </p>
        <div className={styles.footer}>
          <span className={styles.price}>
            {formatPrice(listing.price)} <em>TL/{listing.priceUnit}</em>
          </span>
          {listing.distanceKm !== undefined && listing.locationPrecision !== 'approximate' && (
            <span className={styles.distance}>
              <MapPin /> {tr.common.distanceAway(String(listing.distanceKm))}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
